import type { JSX } from 'react';

/**
 * Die Wahl eines Terminbeginns — Datum und eine Uhrzeit aus dem Raster.
 *
 * ============================================================================
 * EINE AUSWAHL UND KEIN FREIES FELD
 * ============================================================================
 *
 * Auftraggeber am 15.09.2026: „In den eingabemasken für den beginn soll auch
 * nur diese Auswahl möglich sein."
 *
 * Ein `datetime-local` mit `step` prüft erst beim Absenden, und die Uhrzeit
 * lässt sich darin trotzdem frei tippen — der Anwender erfährt vom Raster,
 * wenn es zu spät ist. Eine Liste kennt nur die Zeiten, die es gibt.
 *
 * ============================================================================
 * WOHER DIE ZEITEN KOMMEN
 * ============================================================================
 *
 * TAG-01 in der Fassung nach ÄA-26: Ein Training oder Event beginnt auf einem
 * Vielfachen des Terminrasters, gerechnet ab dem Tagesbeginn des Standorts.
 * Ausgeliefert sind 08:00 Uhr und 45 Minuten; das Tagesende liegt bei 20:30.
 * Daraus ergeben sich siebzehn mögliche Anfangszeiten.
 *
 * DIESE LISTE IST EINE BEQUEMLICHKEIT, KEINE REGEL. Verbindlich prüft die
 * Datenbank (`fach.terminraster_passt`, Migration 0206) — sie kennt den
 * Standort und dessen eigene Werte. Stellt ein Haus sein Raster um, zeigt
 * diese Liste zu viele oder zu wenige Zeiten an, und die Maske bekommt eine
 * Abweisung mit klarer Meldung. Falsch gespeichert wird dadurch nichts.
 */
const TAGESBEGINN_MINUTEN = 8 * 60;
const RASTER_MINUTEN = 45;
const TAGESENDE_MINUTEN = 20 * 60 + 30;

/** 08:00, 08:45, 09:30 … bis zur letzten Zeit vor dem Tagesende. */
export const RASTERZEITEN: readonly string[] = Array.from(
  { length: Math.floor((TAGESENDE_MINUTEN - TAGESBEGINN_MINUTEN) / RASTER_MINUTEN) + 1 },
  (_, i) => {
    const minuten = TAGESBEGINN_MINUTEN + i * RASTER_MINUTEN;
    return `${String(Math.floor(minuten / 60)).padStart(2, '0')}:`
      + String(minuten % 60).padStart(2, '0');
  },
);

/** Die erste mögliche Anfangszeit — die Vorbelegung jeder Maske. */
export const ERSTE_RASTERZEIT = RASTERZEITEN[0]!;

/**
 * Ein Zeitpunkt der Form `2026-09-18T09:30` in seine zwei Teile.
 *
 * Fehlt er oder trägt er eine Uhrzeit, die es im Raster nicht gibt, steht die
 * erste mögliche da: Die Liste darf keinen Eintrag zeigen, den sie nicht
 * hat — sonst stünde dort scheinbar nichts, und der Anwender übersähe es.
 */
export function zerlege(wert: string): { readonly tag: string; readonly zeit: string } {
  const [tag = '', zeit = ''] = wert.split('T');
  return {
    tag,
    zeit: RASTERZEITEN.includes(zeit.slice(0, 5)) ? zeit.slice(0, 5) : ERSTE_RASTERZEIT,
  };
}

export function Beginnwahl({
  kennung,
  beschriftung,
  wert,
  beiAenderung,
  pflicht = false,
  hilfe,
}: {
  /** Grundlage für `id` und `htmlFor` — beide Felder leiten ihre daraus ab. */
  readonly kennung: string;
  readonly beschriftung: string;
  /** `YYYY-MM-DDTHH:MM`, wie es ein `datetime-local` liefert. */
  readonly wert: string;
  readonly beiAenderung: (wert: string) => void;
  readonly pflicht?: boolean;
  readonly hilfe?: string;
}): JSX.Element {
  const { tag, zeit } = zerlege(wert);

  /*
   * ZWEI FELDER, EINE GEMEINSAME UEBERSCHRIFT.
   *
   * Die Beschriftung steht als `span` und nicht als `label`: Ein `label`
   * gehoert zu GENAU EINEM Feld, hier sind es zwei. Die Gruppe traegt sie
   * ueber `aria-labelledby`, jedes Feld seinen eigenen Namen — sonst liest
   * ein Vorleseprogramm zweimal „Beginn" und der Anwender weiss nicht,
   * welches der beiden gerade dran ist.
   */
  return (
    <div className="feld">
      <span className="feld__beschriftung" id={`${kennung}-titel`}>
        {beschriftung}
        {pflicht && <> <span className="feld__pflicht">Pflicht</span></>}
      </span>
      <div className="beginnwahl" role="group" aria-labelledby={`${kennung}-titel`}>
        <input
          id={`${kennung}-tag`}
          type="date"
          className="feld__eingabe"
          aria-label={`${beschriftung} — Datum`}
          value={tag}
          onChange={(e) => beiAenderung(`${e.target.value}T${zeit}`)}
        />
        <select
          id={`${kennung}-zeit`}
          className="feld__eingabe feld__eingabe--schmal"
          aria-label={`${beschriftung} — Uhrzeit`}
          value={zeit}
          onChange={(e) => beiAenderung(`${tag}T${e.target.value}`)}
        >
          {RASTERZEITEN.map((z) => (
            <option key={z} value={z}>
              {z}
            </option>
          ))}
        </select>
      </div>
      <p className="feld__hilfe">
        {hilfe ?? 'Ein Training beginnt alle 45 Minuten ab 8 Uhr.'}
      </p>
    </div>
  );
}
